const template = require('./index.html')
const naturalCompare = require('natural-compare-lite')
const keyboardJS = require('keyboardjs')
const currency = require('@isoftdata/utility-currency')
const makeItInput = require('@isoftdata/input')
const makeItContextMenu = require('@isoftdata/context-menu')
const makePagination = require('@isoftdata/pagination')

async function defaultColumnClicked(clickedColumn, sortDirection) {
	const ractive = this

	if (ractive.findComponent('itPagination') && ractive.get('currentPageNumber') !== ractive.findComponent('itPagination').get('lastPage')) {
		await ractive.set({ currentPageNumber: 1 })
	}

	/* 	await ractive.set({
		sortColumn: clickedColumn,
		sortDirection,
	}) */

	return ractive.doSort({
		rows: ractive.get('filteredRows'),
		sortColumn: clickedColumn,
		sortDirection,
		sameSortOrder: false,
	})
}

function defaultFilter(filter) {
	const ractive = this

	const filterEnabled = ractive.get('filterEnabled')
	const filterProps = ractive.get('filterProps')
	const columnProps = ractive.get('columns')?.map(({ property }) => property)
	let rows = ractive.get('rows')

	if (filterEnabled && filter) {
		let props = filterProps ? filterProps : columnProps

		rows = rows.filter(row => {
			const matches = props
				.filter(key => {
					if (typeof getNestedProperty(row, key) === 'string' || typeof getNestedProperty(row, key) === 'number') {
						return getNestedProperty(row, key)
							.toString()
							.toUpperCase()
							.indexOf(filter.toUpperCase()) > -1
					}
					return false
				})

			return matches.length > 0
		})
	}

	return rows
}

function getNestedProperty(obj, path, defaultValue) {
	if (!path) {
		return
	}
	return path.split('[').reduce((obj, prop) => obj[prop.replace(/\]/g, '')], obj) ?? defaultValue
}

module.exports = function createItTableComponent({ methods = {}, options = {}, stickyHeader = false } = {
	methods: {},
	options: {},
}) {
	// eslint-disable-next-line no-undef
	return Ractive.extend({
		template,
		twoway: false,
		components: {
			filterInput: makeItInput(options.filterInput || { twoway: true, lazy: 50 }),
			itContextMenu: makeItContextMenu(),
			itPagination: makePagination(),
		},
		css: stickyHeader ? `
			table {
				position: relative;
			}

			th {
				background: white;
				position: sticky;
				top: 0;
			}

			table > thead > tr > th {
				z-index: 3;
			}

			th {
				box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4);
			}` : '',
		data() {
			return {
				rows: [],
				filteredRows: [],
				sortDirection: 'ASC',
				sortColumn: '',
				defaultTableClass: 'table table-striped table-bordered table-hover', //These are standard and we want them on by default in all places in the web app
				tableClass: 'table-sm',
				perPageCount: false,
				currentPageNumber: 1,
				currentPageRows: [],
				sortedRows: [],
				showFooter: false,
				footers: [],
				lazySort: false, //when true, the table can only be resorted when the user clicks on a column header(no real time as data changes)
				idProp: false, //required for lazy sorting.
				previousSortOrder: false,
				columns: [],
				filterEnabled: false,
				filterProps: false,
				filter: '',
				filterLabel: 'Filter',
				filterPlaceholder: 'Filter',
				filterDisabled: false,
				filterReadonly: false,
				showFilterLabel: false,
				headerRowClass: 'form-row mr-auto',
				headerColumnClass: 'col',
				filterColumnClass: 'col-lg-2 col-md-4 col-sm-6 align-self-end',
				hiddenColumns: [],
				columnHidingEnabled: false,
				rowSelectionIds: [],
				rowSelectionIdProp: '',
				rowSelectionRequiresModKey: false,
				isSelectionMode: false,
				selectionMode: undefined,
				lastClickedIdForRange: undefined,
				totalItemsCount: 0,
			}
		},
		computed: {
			computedColumns() {
				return this.get('columns').map(col => {
					let thClass = []

					col.headerEllipsis ? thClass.push('text-ellipsis') : null
					col.print === false ? thClass.push('d-print-none') : null

					if (col.align === 'right') {
						thClass.push('text-right')
					} else if (col.align === 'left') {
						thClass.push('text-left')
					} else {
						thClass.push('text-center')
					}

					return {
						...col,
						class: `${col.class} ${thClass.join(' ')}`,
						hideButtonClass: col.unhidable ? 'disabled' : '',
					}
				})
			},
			hidableColumns() {
				return this.get('computedColumns').filter(column => column.unhidable !== true)
			},
			visibleColumns() {
				const hiddenColumns = this.get('hiddenColumns')
				return this.get('columns')?.filter(column => {
					return !hiddenColumns.find(hiddenColumn => hiddenColumn.property == column.property)
				})
			},
			tbodyClass() {
				return this.get('selectionMode') === 'RANGE' ? 'unselectable' : ''
			},
			showPagination() {
				const perPageCount = this.get('perPageCount')
				const rows = this.get('filteredRows')
				const totalItemsCount = this.get('totalItemsCount')

				if (rows && Array.isArray(rows) && perPageCount) {
					return (rows.length > parseInt(perPageCount, 10)) || (totalItemsCount > parseInt(perPageCount, 10))
				}
				return false
			},
			filteredRows() {
				return this.filterMethod(this.get('filter'))
			},
			footers() {
				const rows = this.get('filteredRows')
				const columns = this.get('columns')
				const showFooter = this.get('showFooter')

				if (columns) {
					return columns.map(column => {
						if (column.footer && showFooter) {
							const property = column.footer.altProperty ? column.footer.altProperty : column.property
							const mathFunctions = [ 'AVG', 'SUM' ]

							let columnValues = rows.map(row => {
								if (mathFunctions.includes(column.footer.fn)) {
									return parseFloat(getNestedProperty(row, property, 0))
								}
								return getNestedProperty(row, property)
							})

							if (column.footer.requiredValue) {
								columnValues = columnValues.filter(value => value === column.footer.requiredValue)
							}

							let value

							switch (column.footer.fn) {
								case 'COUNT':
									value = columnValues.length
									break
								case 'AVG':
									value = (columnValues.reduce((sum, val) => sum + val) / columnValues.length).toFixed(2)
									break
								case 'SUM':
									value = columnValues.reduce((sum, val) => sum + val)
									break
							}

							return column.footer.formatCurrency ? currency.format(value, {}) : value
						}
						return ''
					})
				}
				return false
			},
		},
		columnClickedMethod: methods.columnClicked || defaultColumnClicked,
		filterMethod: methods.filter || defaultFilter,
		async columnClicked(clickedColumn) {
			const ractive = this

			const currentSortDirection = ractive.get('sortDirection')
			const currentSortColumn = ractive.get('sortColumn')

			const sortDirection = (currentSortDirection === 'ASC'
				&& clickedColumn.property === currentSortColumn.property) ? 'DESC' : 'ASC'

			await ractive.columnClickedMethod(clickedColumn, sortDirection)

			return ractive.set({
				sortColumn: clickedColumn,
				sortDirection,
			})
		},
		rowClick(clickedId) {
			const ractive = this
			const selectionIdProp = ractive.get('rowSelectionIdProp')

			if (selectionIdProp) {
				let selectionMode = ractive.get('selectionMode')
				const sortedRows = ractive.get('sortedRows')
				const selectedIds = ractive.get('rowSelectionIds')
				const lastClickedIdForRange = ractive.get('lastClickedIdForRange')
				const rowSelectionRequiresModKey = ractive.get('rowSelectionRequiresModKey')

				let newSelectedIds = []

				if ((!selectionMode && !rowSelectionRequiresModKey)) {
					selectionMode = 'SINGLE'
				}

				if (selectionMode === 'SINGLE') {
					newSelectedIds = selectedIds
					const foundInExistingSelections = selectedIds.find(id => {
						return id === clickedId
					})

					if (foundInExistingSelections) {
						newSelectedIds = newSelectedIds.filter(id => {
							return id !== clickedId
						})
					} else {
						newSelectedIds = newSelectedIds.concat(clickedId)
					}
				} else if (selectionMode === 'RANGE') {
					if (selectedIds.length < 1) {
						newSelectedIds = [ clickedId ]
					} else {
						newSelectedIds = selectedIds
						const clickedItemIndex = sortedRows.findIndex(item => {
							return item[selectionIdProp] === clickedId
						})

						const lastSelectedIndex = sortedRows.findIndex(item => {
							return item[selectionIdProp] === lastClickedIdForRange
						})

						let selectionDirection

						if (lastSelectedIndex > clickedItemIndex) {
							selectionDirection = 'UP'
						} else if (lastSelectedIndex < clickedItemIndex) {
							selectionDirection = 'DOWN'
						}

						if (selectionDirection) {
							sortedRows.forEach((item, index) => {
								if (selectionDirection === 'UP' &&
									index <= lastSelectedIndex &&
									index >= clickedItemIndex) {
									newSelectedIds = newSelectedIds.concat(item[selectionIdProp])
								} else if (selectionDirection === 'DOWN' &&
									index >= lastSelectedIndex &&
									index <= clickedItemIndex) {
									newSelectedIds = newSelectedIds.concat(item[selectionIdProp])
								}
							})
						}
					}
				} else {
					return
				}

				const clickedIdWasAdded = newSelectedIds.find(id => id === clickedId)

				let lastClickedId

				if (newSelectedIds.length === 0) {
					lastClickedId = undefined
				} else if (selectionMode === 'SINGLE' &&
					clickedIdWasAdded) {
					lastClickedId = clickedId
				} else {
					lastClickedId = lastClickedIdForRange
				}

				ractive.set({
					rowSelectionIds: [ ...new Set(newSelectedIds) ],
					lastClickedIdForRange: lastClickedId,
				})
			}
		},
		setPageVisibleByItemIndex(index) {
			if (index > -1) {
				this.findComponent('itPagination').setPageVisibleByItemIndex(index)
			}
		},
		setPageVisibleByItemId({ id, keyName }) {
			const index = this.get('sortedRows').findIndex(item => item[keyName] === id)

			if (index > -1) {
				this.findComponent('itPagination').setPageVisibleByItemIndex(index)
			}
		},
		doSort({ rows, sortColumn, sortDirection, sameSortOrder }) {
			const ractive = this

			const idProp = ractive.get('idProp')
			const lazySort = ractive.get('lazySort')
			let previousSortOrder = ractive.get('previousSortOrder')

			const keepSameSortOrder = !!(lazySort && sameSortOrder && idProp)
			const hasPreviousSort = previousSortOrder && previousSortOrder.length > 0

			let sortProp

			if (keepSameSortOrder && hasPreviousSort) {
				sortProp = 'order'
				sortColumn = ractive.get('previousSortColumn')
				sortDirection = 'ASC' //force ASC for keeping the same order because we defined the order in previousSortOrder

				rows = rows.map(row => {
					const foundItem = previousSortOrder.find(previousSortOrderRow => previousSortOrderRow[idProp] === row[idProp])

					return { ...row, order: foundItem ? foundItem.order : 0 }
				})
			} else {
				sortProp = sortColumn.property
			}

			rows = rows.map((row, index) => {
				return { ...row, originalIndex: index }
			})

			const sortedRows = rows.slice()
				.sort(sortColumn.sortType === 'alphaNum' ? alphaNumSort : standardSort)

			return ractive.set({
				previousSortOrder: sortedRows.map((row, index) => ({ ...row, order: index })),
				previousSortDirection: sortDirection,
				previousSortColumn: sortColumn,
				sortedRows,
			})

			function standardSort(a, b) {
				a = getNestedProperty(a, sortProp, '')
				b = getNestedProperty(b, sortProp, '')

				if (typeof a === 'string') {
					a = a.toUpperCase()
				}
				if (typeof b === 'string') {
					b = b.toUpperCase()
				}

				if (a < b) {
					return sortDirection === 'ASC' ? -1 : 1
				}
				if (a > b) {
					return sortDirection === 'ASC' ? 1 : -1
				}

				return 0
			}

			function alphaNumSort(a, b) {
				const compareResults = naturalCompare(getNestedProperty(a, sortProp), getNestedProperty(b, sortProp))

				return sortDirection === 'ASC' ? compareResults : compareResults * -1
			}
		},
		oninit() {
			const ractive = this

			if (ractive.get('rowSelectionIdProp')) {
				function handleModifierKey(e) {
					ractive.set('isSelectionMode', e.type === 'keydown' && e.metaKey).then(() => {
						ractive.set('selectionMode', ractive.get('isSelectionMode') ? 'SINGLE' : null)
					})
				}

				function handleShiftKey(e) {
					ractive.set('isSelectionMode', e.type === 'keydown').then(() => {
						ractive.set('selectionMode', ractive.get('isSelectionMode') ? 'RANGE' : null)
					})
				}

				keyboardJS.bind('mod', e => {
					handleModifierKey(e)
				}, e => {
					handleModifierKey(e)
				})

				keyboardJS.bind('shift', e => {
					//e.preventDefault()
					handleShiftKey(e)
				}, e => {
					//e.preventDefault()
					handleShiftKey(e)
				})
			}

			ractive.set({ sortedRows: ractive.get('rows') })

			ractive.on('hideColumn', (context, event, column) => {
				ractive.push('hiddenColumns', column)
			})

			ractive.on('showColumn', (context, event, column) => {
				const columnIndex = ractive.get('hiddenColumns')
					.findIndex(hiddenColumn => hiddenColumn.property == column.property)

				if (columnIndex > -1) {
					ractive.splice('hiddenColumns', columnIndex, 1)
				}
			})

			ractive.observe('filteredRows', rows => {
				rows = (rows && Array.isArray(rows)) ? rows : []

				ractive.doSort({
					rows,
					sortColumn: ractive.get('sortColumn'),
					sortDirection: ractive.get('sortDirection'),
					sameSortOrder: true,
				})
			})

			ractive.observe('keyboard', val => {
				if (val) {
					keyboardJS.setContext('itTable')
				} else if (val !== undefined) {
					keyboardJS.setContext(null)
				}
			})

			keyboardJS.withContext('itTable', () => {
				const idName = ractive.get('idName')
				if (idName) {
					keyboardJS.bind('up', () => {
						ractive.fire('selection-change', findPreviousOrNextItemById(ractive.get('selectedId'), idName, false))
					})

					keyboardJS.bind('down', () => {
						ractive.fire('selection-change', findPreviousOrNextItemById(ractive.get('selectedId'), idName))
					})
				}

				keyboardJS.bind('enter', () => ractive.fire('enter-pressed'))

				keyboardJS.bind('pageup', () => {
					if (ractive.get('currentPageNumber') > 1) {
						ractive.set({ currentPageNumber: ractive.get('currentPageNumber') - 1 })
					}
				})

				keyboardJS.bind('pagedown', () => {
					if (ractive.get('currentPageNumber') !== ractive.findComponent('itPagination').get('lastPage')) {
						ractive.set({ currentPageNumber: ractive.get('currentPageNumber') + 1 })
					}
				})
			})

			function findPreviousOrNextItemById(id, idName, findNext = true) {
				const currentPageRows = ractive.get('currentPageRows')
				id = parseInt(id, 10)

				if (!id) {
					return currentPageRows[0][idName]
				}

				const indexOfCurrentItem = currentPageRows.findIndex(item => item[idName] == id)

				let indexOfNewSelection = findNext ? indexOfCurrentItem + 1 : indexOfCurrentItem - 1

				//In case they pass us an id that isn't in the array
				if (indexOfNewSelection < 0) {
					indexOfNewSelection = 0
				}

				if (indexOfNewSelection < 0 || indexOfNewSelection == currentPageRows.length) {
					indexOfNewSelection = indexOfCurrentItem
				}

				return currentPageRows[indexOfNewSelection][idName]
			}

			const defaultSortColumn = ractive.get('columns')
				.find(column => column.defaultSortColumn)

			if (defaultSortColumn) {
				const { defaultSortDirection } = defaultSortColumn
				const sortDirection = defaultSortDirection ? defaultSortDirection : 'ASC'

				ractive.set({
					sortColumn: defaultSortColumn,
					sortDirection,
				}).then(() => {
					ractive.doSort({
						rows: ractive.get('filteredRows'),
						sortColumn: defaultSortColumn,
						sortDirection,
						sameSortOrder: false,
					})
				})
			}
		},
		onteardown() {
			const ractive = this
			if (ractive.get('keyboard')) {
				keyboardJS.reset()
			}
		},
	})
}
